import type { DistanceUnits, LoggedSet, Units } from '~/data/types'

/** The regions that lift in pounds; everyone else gets kilograms. */
const POUND_REGIONS = new Set(['US', 'LR', 'MM'])

/** The regions that measure a run in miles; everyone else gets kilometres. */
const MILE_REGIONS = new Set(['US', 'LR', 'MM', 'GB'])

function regionOf(language: string): string | undefined {
  try {
    return new Intl.Locale(language).maximize().region
  } catch {
    return undefined
  }
}

/** Until changed, units follow the device's region. */
export function regionDefaultUnits(language: string): Units {
  const region = regionOf(language)
  return region !== undefined && POUND_REGIONS.has(region) ? 'lb' : 'kg'
}

/**
 * And distance follows it separately. Britain lifts in kilograms and drives in miles, which is
 * exactly why these are two questions rather than one.
 */
export function regionDefaultDistanceUnits(language: string): DistanceUnits {
  const region = regionOf(language)
  return region !== undefined && MILE_REGIONS.has(region) ? 'mi' : 'km'
}

const KG_PER_LB = 0.45359237

export function kgToDisplay(weightKg: number, units: Units): number {
  const value = units === 'kg' ? weightKg : weightKg / KG_PER_LB
  return Math.round(value * 10) / 10
}

export function displayToKg(value: number, units: Units): number {
  return units === 'kg' ? value : value * KG_PER_LB
}

export function formatWeight(weightKg: number, units: Units): string {
  return `${kgToDisplay(weightKg, units)} ${units}`
}

/**
 * What a lift came to against the body that lifted it — "1.25× bodyweight". Unit-free on
 * purpose: it is one weight over another, and the answer is the same number in kilograms as it
 * is in pounds.
 *
 * Null wherever the comparison would be a lie rather than a fact: a set that records no weight
 * has nothing to compare, a day with nothing on the scale has nothing to compare it against,
 * and a weight of nothing over a body is zero — true, and not worth a word.
 */
export function bodyweightRatioSaid(
  liftedKg: number | null | undefined,
  bodyweightKg: number | null | undefined,
): string | null {
  if (liftedKg == null || liftedKg <= 0 || bodyweightKg == null || bodyweightKg <= 0) {
    return null
  }
  return bodyweightRatioText(liftedKg / bodyweightKg)
}

/**
 * A ratio already worked out, said the one way the app says it. Rounded here rather than at
 * every place that shows one, so the number on a set and the number on a record agree.
 *
 * Null under a hundredth of the body, which is not a ratio anybody wants read out to them.
 */
export function bodyweightRatioText(ratio: number): string | null {
  const rounded = Math.round(ratio * 100) / 100
  if (rounded <= 0) {
    return null
  }
  return `${rounded}× bodyweight`
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)} s`
  }
  const minutes = Math.floor(seconds / 60)
  const rest = Math.round(seconds % 60)
  return `${minutes}:${String(rest).padStart(2, '0')} min`
}

const METERS_PER_MILE = 1609.344
const METERS_PER_YARD = 0.9144

export function metersToDisplay(meters: number, units: DistanceUnits): number {
  const value = units === 'km' ? meters / 1000 : meters / METERS_PER_MILE
  return Math.round(value * 100) / 100
}

export function displayToMeters(value: number, units: DistanceUnits): number {
  return units === 'km' ? value * 1000 : value * METERS_PER_MILE
}

/**
 * A distance, in the small unit until there is a whole big one to say. Both systems read the
 * same way — metres under a kilometre, yards under a mile — so the shape of the line does not
 * change when the setting does.
 */
export function formatDistance(meters: number, units: DistanceUnits): string {
  if (units === 'mi') {
    return meters < METERS_PER_MILE
      ? `${Math.round(meters / METERS_PER_YARD)} yd`
      : `${Math.round((meters / METERS_PER_MILE) * 10) / 10} mi`
  }
  return meters < 1000 ? `${Math.round(meters)} m` : `${Math.round(meters / 100) / 10} km`
}

/** A pace, said per whichever big unit distance is being read in. */
export function formatPace(secondsPerKm: number, units: DistanceUnits): string {
  const per = units === 'km' ? secondsPerKm : secondsPerKm * (METERS_PER_MILE / 1000)
  return `${formatDuration(per)} / ${units}`
}

/**
 * The numbers a set actually carries. Which ones those are is the set's own business, not its
 * exercise's: a pull up records weight, but only the ones done with a belt have any.
 */
export function formatSetNumbers(
  set: LoggedSet,
  units: Units,
  distanceUnits: DistanceUnits = 'km',
): string {
  const weight = set.weightKg === null ? null : formatWeight(set.weightKg, units)

  // Weight against reps multiplies out; reps alone have nothing to multiply.
  const load =
    weight !== null && set.reps !== null
      ? `${weight} × ${set.reps}`
      : (weight ?? (set.reps === null ? null : `${set.reps} reps`))

  const duration = set.durationSeconds === null ? null : formatDuration(set.durationSeconds)
  const distance =
    set.distanceMeters === null ? null : formatDistance(set.distanceMeters, distanceUnits)
  const covered =
    distance !== null && duration !== null ? `${distance} in ${duration}` : (distance ?? duration)

  return [load, covered].filter((part) => part !== null).join(', ') || '—'
}

const CM_PER_INCH = 2.54

/** A height in centimetres as feet and the inches left over, to the nearest inch. */
export function cmToFeetInches(cm: number): { feet: number; inches: number } {
  const inches = Math.round(cm / CM_PER_INCH)
  return { feet: Math.floor(inches / 12), inches: inches % 12 }
}

/** A height given in feet and inches, kept as the nearest whole centimetre. */
export function feetInchesToCm(feet: number, inches: number): number {
  return Math.round((feet * 12 + inches) * CM_PER_INCH)
}

/**
 * A height as it is read, following the distance units — see Units in the root spec:
 * centimetres with kilometres, feet and inches with miles.
 */
export function formatHeight(cm: number, units: DistanceUnits): string {
  if (units === 'km') {
    return `${cm} cm`
  }
  const { feet, inches } = cmToFeetInches(cm)
  return `${feet} ft ${inches} in`
}
